import Group from 'components/utils/Group'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import Image from 'components/Common/Image'
import Sheet from 'components/utils/Sheet'
import styled from 'styled-components'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import GooglePlacePriceLevel from 'tripPlanner/components/GooglePlacePriceLevel'
import GoogleRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import type { ExploreItem } from 'tripPlanner/types/common'
import { usePlaceRating } from 'tripPlanner/hooks/Google/usePlaceRating'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import useNavigateTo from 'hooks/useNavigateTo'
import { getTripURL } from 'tripPlanner/utils'
import ModalContext from 'contexts/ModalContext'
import ExploreItemDetails from 'tripPlanner/components/TripModal/ExploreItems/ExploreItemDetails'
import GeoContext from 'contexts/geoContext'
import { getUnit, kmToMiles } from 'lib/geo/distanceUnits'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'

interface Props {
  item: ExploreItem
  distance?: number
  showDistance: boolean
  tripId?: string
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${rem(100)} 1fr;
  grid-template-rows: 1fr;
  height: ${rem(115)};
  width: ${rem(312)};
`
const StyledSheetTransparent = styled(Sheet)`
  background: rgba(255,255,255, 0.16);
`
const StyledImage = styled(Image)`
  width: 100%;
  height: ${rem(115)};
  object-fit: cover;
`
function TripCarouselCards({ item, distance, showDistance, tripId }: Props) {
  const intl = useIntl()
  const { data: place } = usePlaceRating(item.id)
  const regionText = item.place.region?.split(',')[0]
  const navigateTo = useNavigateTo()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)

  const handleClick = useCallback(() => {
    if (!tripId) return

    navigateTo(getTripURL(tripId))
    showModal(<ExploreItemDetails item={item} tripId={tripId} />)
  }, [tripId, item, navigateTo, showModal])

  const formatDistance = useCallback((distanceKm: number) => {
    if (!distanceKm) return ''

    const unit = getUnit(currentRegionCode)
    if (unit === 'mi') {
      const miles = kmToMiles(distanceKm)
      const roundedMiles = Math.round(miles * 10) / 10
      return `${roundedMiles}mi`
    }

    return `${distanceKm.toFixed(1)}km`
  }, [currentRegionCode])

  return (
    <>
      {item.photo?.photoUri && <Clickable onClick={handleClick}>
        <Grid>
          <StyledImage image={{ url: item.photo?.photoUri }} />
          <StyledSheetTransparent padding="8 12" paletteType="inverse">
            <Group direction="vertical" gap={12}>
              {showDistance && <BodyTextBlock startIcon={<LineMapMarkerIcon colour="highlight-primary" />} variant="small" colour="highlight-primary">
                {intl.formatMessage({
                  id: 'Y5Puo7JpVvZC9NO9Cm0P',
                  defaultMessage: '{distance} from hotel',
                  description: 'Label showing the distance from the hotel to a nearby point of interest or landmark',
                }, { distance: formatDistance(distance || 0) })}
              </BodyTextBlock>}
              {!showDistance && item.place.region && <BodyTextBlock startIcon={<LineMapMarkerIcon colour="highlight-primary" />} variant="small" colour="highlight-primary">{regionText}</BodyTextBlock>}
              <Group direction="vertical" gap={2}>
                <BodyText variant="medium" weight="bold" lineClamp={2}> {item.name}</BodyText>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <GooglePlacePriceLevel priceLevel={item.priceLevel} size="small" />
                  {item.priceLevel && <BodyText variant="small" colour="neutral-three">{intl.formatMessage(middleDotSeparatorMessage)}</BodyText>}
                  {!!place?.rating && !!place?.userRatingCount && <GoogleRatingDisplay rating={place.rating} ratingsCount={place.userRatingCount} size="S" />}
                </Group>
              </Group>
            </Group>
          </StyledSheetTransparent>
        </Grid>
      </Clickable>}
    </>
  )
}

export default TripCarouselCards
